import React from 'react';
import Image from "next/image";
import willImg from "@/app/blog/_assets/images/authors/will.jpg";
import jaredImg from "@/app/blog/_assets/images/authors/jared.jpg";
import calvinImg from "@/app/blog/_assets/images/authors/calvin.webp";

// ==================================================================================================================================================================
// BLOG CATEGORIES 🏷️
// ==================================================================================================================================================================

export const categorySlugs = {
  lyricTutorials: "lyric-tutorials",
  musicProduction: "music-production",
  aiMusicTools: "ai-music-tools",
  caseStudies: "case-studies",
  vocalProcessing: "vocal-processing",
  creativeProcess: "creative-process",
  industryNews: "industry-news",
  toolUpdates: "tool-updates",
};

export interface categoryType {
  slug: string;          // URL-friendly identifier
  title: string;         // Full category title
  titleShort: string;    // Short version of the title for badges
  description: string;   // Full description
  descriptionShort: string; // Short description
}

// Instead of using any for category, we can properly type it
export type articleType = {
  slug: string;
  title: string; 
  description: string;
  categories: Array<categoryType>;  // Modified to use the proper type instead of undefined union
  author: {
    slug: string;
    name: string;
    job: string;
    description: string;
    avatar: any;
    socials: Array<{
      name: string;
      icon: React.ReactNode;
      url: string;
    }>;
  };  // Removed undefined union
  publishedAt: string;
  image: {
    src: any;
    urlRelative: string;
    alt: string;
  };
  content: React.ReactNode;
};

export const categories = [
  {
    slug: categorySlugs.lyricTutorials,
    title: "Lyric Changing Tutorials",
    titleShort: "Tutorials",
    description: "Step-by-step guides on how to effectively change lyrics in songs using AI tools while maintaining vocal quality, timing, and natural flow.",
    descriptionShort: "Master the art of changing lyrics in any song.",
  },
  {
    slug: categorySlugs.musicProduction,
    title: "Music Production Tips",
    titleShort: "Production",
    description: "Professional techniques for working with vocal stems, mixing modified vocals, and achieving studio-quality results with lyric changes.",
    descriptionShort: "Professional tips for vocal editing and production.",
  },
  {
    slug: categorySlugs.aiMusicTools,
    title: "AI Music Technology",
    titleShort: "AI Tools",
    description: "Explore the latest AI technologies for music creation, vocal synthesis, and lyric modification including reviews and comparisons.",
    descriptionShort: "Cutting-edge AI tools for music creators.",
  },
  {
    slug: categorySlugs.caseStudies,
    title: "Success Stories",
    titleShort: "Case Studies",
    description: "Real examples of artists and creators using ChangeLyric to transform songs, create parodies, and produce unique content.",
    descriptionShort: "See how creators are transforming music.",
  },
  {
    slug: categorySlugs.vocalProcessing,
    title: "Vocal Processing Guide",
    titleShort: "Vocals",
    description: "Deep dives into vocal separation, processing techniques, and maintaining audio quality when changing lyrics in songs.",
    descriptionShort: "Master vocal separation and processing.",
  },
  {
    slug: categorySlugs.creativeProcess,
    title: "Creative Inspiration",
    titleShort: "Creativity",
    description: "Ideas and inspiration for creative lyric changes, from parodies and translations to completely reimagined songs.",
    descriptionShort: "Get inspired to create unique lyric versions.",
  },
  {
    slug: categorySlugs.industryNews,
    title: "Music Industry News",
    titleShort: "Industry",
    description: "Latest developments in music AI, copyright considerations, and industry trends relevant to lyric modification technology.",
    descriptionShort: "Stay updated on music AI developments.",
  },
  {
    slug: categorySlugs.toolUpdates,
    title: "ChangeLyric Updates",
    titleShort: "Updates",
    description: "New features, improvements, and announcements about the ChangeLyric platform and related tools.",
    descriptionShort: "Latest updates and features from ChangeLyric.",
  },
];

// ==================================================================================================================================================================
// BLOG AUTHORS 📝
// ==================================================================================================================================================================

const socialIcons = {
  twitter: {
    name: "Twitter",
    svg: (
      <svg
        version="1.1"
        id="svg5"
        x="0px"
        y="0px"
        viewBox="0 0 1668.56 1221.19"
        className="w-9 h-9"
      >
        <g id="layer1" transform="translate(52.390088,-25.058597)">
          <path
            id="path1009"
            d="M283.94,167.31l386.39,516.64L281.5,1104h87.51l340.42-367.76L984.48,1104h297.8L874.15,558.3l361.92-390.99   h-87.51l-313.51,338.7l-253.31-338.7H283.94z M412.63,231.77h136.81l604.13,807.76h-136.81L412.63,231.77z"
          />
        </g>
      </svg>
    ),
  },
  linkedin: {
    name: "LinkedIn",
    svg: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6"
        viewBox="0 0 24 24"
      >
        <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
      </svg>
    ),
  },
};

const authorSlugs = {
  will: "will",
  jared: "jared",
  calvin: "calvin",
};

export const authors = [
  {
    slug: authorSlugs.will,
    name: "William H. Mason",
    job: "Founder of ChangeLyric",
    description: "William (Will) is the founder of ChangeLyric. With expertise in AI music technology and audio processing, Will is dedicated to empowering musicians and creators with innovative tools to transform songs and unleash their creative potential.",
    avatar: willImg,
    socials: [
      {
        name: socialIcons.linkedin.name,
        icon: socialIcons.linkedin.svg,
        url: "https://www.linkedin.com/in/whmason1994/",
      },
    ],
  },
  {
    slug: authorSlugs.jared,
    name: "Jared Simon",
    job: "Producer at ChangeLyric",
    description:
      "Jared Simon, known on stage as DJ NoMis, is a New York-based DJ, producer, and audio engineer. He produces custom songs for ChangeLyric's done-for-you service, reimagining vocals and rebuilding tracks for clients marking weddings, anniversaries, and other milestones. Jared is the founder of Nomaste Studios and a partner at Music Made Pro, with original productions and remixes released under DJ NoMis and Nomaste.",
    avatar: jaredImg,
    socials: [
      {
        name: socialIcons.linkedin.name,
        icon: socialIcons.linkedin.svg,
        url: "https://www.linkedin.com/in/jared-s-365a38108/",
      },
    ],
  },
  {
    slug: authorSlugs.calvin,
    name: "Calvin Marshall",
    job: "Producer at ChangeLyric",
    description:
      "Calvin Marshall, known professionally as Caylo the Engineer, is a New Orleans-based audio engineer, mixer, and vocalist with over 20 years of experience. He owns Caylos Mix and Master, and his work has been featured on mainstream radio across North America. Calvin engineers custom song projects for ChangeLyric's done-for-you service.",
    avatar: calvinImg,
    socials: [
      {
        name: socialIcons.linkedin.name,
        icon: socialIcons.linkedin.svg,
        url: "https://www.linkedin.com/in/calvin-marshall-9322b9117/",
      },
    ],
  },
];

// ==================================================================================================================================================================
// BLOG ARTICLES 📚
// ==================================================================================================================================================================

// Shared styles for blog post content
export const styles = {
  h1: "text-3xl lg:text-5xl font-bold tracking-tight mb-6 text-base-content",
  h2: "text-2xl lg:text-4xl font-bold tracking-tight mb-4 text-base-content",
  h3: "text-xl lg:text-2xl font-bold tracking-tight mb-2 text-base-content",
  p: "text-base-content/80 leading-relaxed mb-4",
  ul: "list-none text-base-content/80 leading-relaxed mb-4 space-y-2",
  li: "relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-base-content/80",
  code: "text-sm font-mono bg-base-300 text-base-content/80 p-6 rounded-box my-4 overflow-x-scroll select-all",
  codeInline: "text-sm font-mono bg-base-300 px-1 py-0.5 rounded-box select-all",
  a: "text-primary hover:text-primary/80 underline",
  blockquote: "border-l-4 border-primary pl-4 py-2 mb-4 text-base-content/80 italic",
};

// This is a placeholder that will be populated at build time
export let articles: articleType[] = [];